import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  IconButton,
  Typography,
  Box,
  Chip,
  Divider,
  TextField,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import {
  Group,
  Person,
  PersonAdd,
  PersonRemove,
  AdminPanelSettings,
  Support
} from '@mui/icons-material';
import { conversationsAPI } from '../../services/api';

interface Participant {
  id: number;
  name: string;
  email: string;
  role: 'agent' | 'admin' | 'super_admin';
  avatar_url?: string;
  status: 'online' | 'offline' | 'busy';
  last_seen_at?: string;
}

interface ParticipantsDialogProps {
  open: boolean;
  onClose: () => void;
  conversationId: number;
  currentParticipants: Participant[];
  onParticipantsUpdated: () => void;
}

const ParticipantsDialog: React.FC<ParticipantsDialogProps> = ({
  open,
  onClose,
  conversationId,
  currentParticipants,
  onParticipantsUpdated
}) => {
  const [participants, setParticipants] = useState<Participant[]>(currentParticipants);
  const [availableAgents, setAvailableAgents] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (open) {
      setParticipants(currentParticipants);
      loadAvailableAgents();
    }
  }, [open, currentParticipants, conversationId]);

  const loadAvailableAgents = async () => {
    try {
      setLoading(true);
      const response = await conversationsAPI.getAvailableAgents(conversationId);
      setAvailableAgents(response.agents || []);
    } catch (error) {
      console.error('Failed to load available agents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddParticipant = async (agent: Participant) => {
    try {
      setAdding(true);
      // Add participant to conversation
      await conversationsAPI.addParticipant(conversationId, agent.id);
      
      setParticipants(prev => [...prev, agent]);
      onParticipantsUpdated();
    } catch (error) {
      console.error('Failed to add participant:', error);
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveParticipant = async (participantId: number) => {
    try {
      await conversationsAPI.removeParticipant(conversationId, participantId);
      
      setParticipants(prev => prev.filter(p => p.id !== participantId));
      onParticipantsUpdated();
    } catch (error) {
      console.error('Failed to remove participant:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return '#4caf50';
      case 'busy': return '#ff9800';
      case 'offline': return '#9e9e9e';
      default: return '#9e9e9e';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
      case 'super_admin':
        return <AdminPanelSettings fontSize="small" />;
      default:
        return <Support fontSize="small" />;
    }
  };

  const formatLastSeen = (lastSeenAt?: string) => {
    if (!lastSeenAt) return 'Never';
    
    const lastSeen = new Date(lastSeenAt);
    const now = new Date();
    const diffInHours = (now.getTime() - lastSeen.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return `${Math.floor(diffInHours / 24)}d ago`;
    }
  };

  // Filter out agents already in conversation
  const availableToAdd = availableAgents.filter(
    agent => !participants.some(p => p.id === agent.id)
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Group />
          Conversation Participants
          <Chip 
            size="small" 
            label={`${participants.length} ${participants.length === 1 ? 'participant' : 'participants'}`}
            color="primary"
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Current Participants */}
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Current Participants
            </Typography>
            
            {participants.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No participants in this conversation.
              </Typography>
            ) : (
              <List sx={{ mb: 2 }}>
                {participants.map((participant) => (
                  <ListItem key={participant.id}>
                    <ListItemAvatar>
                      <Box sx={{ position: 'relative' }}>
                        <Avatar
                          src={participant.avatar_url}
                          sx={{ bgcolor: 'primary.main' }}
                        >
                          {participant.name.charAt(0)}
                        </Avatar>
                        {/* Status indicator */}
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: getStatusColor(participant.status),
                            border: '2px solid white',
                          }}
                        />
                      </Box>
                    </ListItemAvatar>
                    
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          {participant.name}
                          {getRoleIcon(participant.role)}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {participant.email}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {participant.status === 'online' ? 'Online' : `Last seen ${formatLastSeen(participant.last_seen_at)}`}
                          </Typography>
                        </Box>
                      }
                    />
                    
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveParticipant(participant.id)}
                        color="error"
                        size="small"
                      >
                        <PersonRemove />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Add Participants */}
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Add Participants
            </Typography>
            
            {availableToAdd.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No additional agents available to add.
              </Typography>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select agents to add to this conversation:
                </Typography>
                
                <List>
                  {availableToAdd.map((agent) => (
                    <ListItem key={agent.id}>
                      <ListItemAvatar>
                        <Box sx={{ position: 'relative' }}>
                          <Avatar
                            src={agent.avatar_url}
                            sx={{ bgcolor: 'secondary.main' }}
                          >
                            {agent.name.charAt(0)}
                          </Avatar>
                          {/* Status indicator */}
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 0,
                              right: 0,
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: getStatusColor(agent.status),
                              border: '2px solid white',
                            }}
                          />
                        </Box>
                      </ListItemAvatar>
                      
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            {agent.name}
                            {getRoleIcon(agent.role)}
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {agent.email}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {agent.status === 'online' ? 'Online' : `Last seen ${formatLastSeen(agent.last_seen_at)}`}
                            </Typography>
                          </Box>
                        }
                      />
                      
                      <ListItemSecondaryAction>
                        <Button
                          size="small"
                          startIcon={<PersonAdd />}
                          onClick={() => handleAddParticipant(agent)}
                          disabled={adding}
                        >
                          Add
                        </Button>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ParticipantsDialog;